import React, { Fragment, useContext, useState } from "react";
import ReactDOMServer from "react-dom/server";
import clsx from "clsx";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Collapse,
  IconButton,
} from "@material-ui/core";
import { Feature } from "geojson";
import { v4 as uuidv4 } from "uuid";
import turfLength from "@turf/length";
import turfAlong from "@turf/along";
import { DesignContext } from "../../context/design";
import EquipDesignWorkfunction from "./equip-design-workfunction";
import EquipDesignComment from "./equip-design-comment";
import EquipDesignImages from "./equip-design-images";
import {
  CableAerialValueList,
  CableTypeList,
  mediumVoltageCableTypeList,
} from "../../constant/equip-options";
import EquipDesignAppearance from "./equip-design-appearance";
import { ExpandMoreRounded, Clear, Done } from "@material-ui/icons";
import EquipGenSymbol from "./equip-gen-symbol";
import { LangContext } from "../../context/lang";
import { scaleTextToZoomLevel } from "../../constant/commons";

const useStyle = makeStyles((theme: Theme) => ({
  equipSetting: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  equip: {
    width: theme.spacing(14),
    height: theme.spacing(14),
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    fontFamily: ["Montserrat-Medium", "Kanit-Medium"].join(","),
    fontSize: 18,
  },
  radioSize: {
    paddingLeft: theme.spacing(2),
    flexDirection: "row",
  },
  radioDiv: { width: `calc(50% - ${theme.spacing(2)}px)` },
  btn: { textTransform: "unset" },
  gridSave: {
    padding: `0px !important`,
    display: "flex",
    justifyContent: "flex-end",
  },
  successColor: { color: theme.palette.success.main },
}));

const EquipDesignCable = () => {
  const classes = useStyle();
  const [appeashow, setappeashow] = useState<boolean>(false);
  const { lang } = useContext(LangContext);
  const { map, event, setevent } = useContext(DesignContext);
  const { feature, designEquips } = event;

  const _toggleRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
    let mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    setevent({
      ...event,
      feature: {
        ...event.feature,
        properties: {
          ...event.feature?.properties,
          [e.currentTarget.name]: e.currentTarget.value,
        },
      },
    });
    draw.setFeatureProperty(
      feature?.id,
      e.currentTarget.name,
      e.currentTarget.value
    );
  };

  const _toggleOption = (e: React.ChangeEvent<HTMLInputElement>) => {
    let mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    if (e.currentTarget.name === "aerial") {
      if (feature) {
        setevent({
          ...event,
          feature: {
            ...event.feature,
            properties: {
              ...event.feature?.properties,
              cableType: e.currentTarget.name,
              aerialSize: CableAerialValueList[0].value,
              mediumVoltageCableType: undefined,
            },
          },
        });
        draw.setFeatureProperty(feature.id, "cableType", e.currentTarget.name);
        draw.setFeatureProperty(
          feature.id,
          "aerialSize",
          CableAerialValueList[0].value
        );
        draw.setFeatureProperty(
          feature.id,
          "mediumVoltageCableType",
          undefined
        );
      }
    } else if (e.currentTarget.name === "mediumVoltageCable") {
      if (feature) {
        setevent({
          ...event,
          feature: {
            ...event.feature,
            properties: {
              ...event.feature?.properties,
              cableType: e.currentTarget.name,
              aerialSize: undefined,
              mediumVoltageCableType: mediumVoltageCableTypeList[0].value,
            },
          },
        });
        draw.setFeatureProperty(feature.id, "cableType", e.currentTarget.name);
        draw.setFeatureProperty(feature.id, "aerialSize", undefined);
        draw.setFeatureProperty(
          feature.id,
          "mediumVoltageCableType",
          mediumVoltageCableTypeList[0].value
        );
      }
    } else {
      if (feature && feature.properties) {
        draw.setFeatureProperty(feature.id, "cableType", e.currentTarget.name);
        draw.setFeatureProperty(feature.id, "aerialSize", undefined);
        draw.setFeatureProperty(
          feature.id,
          "mediumVoltageCableType",
          undefined
        );
        setevent({
          ...event,
          feature: {
            ...event.feature,
            properties: {
              ...event.feature?.properties,
              cableType: e.currentTarget.name,
              aerialSize: undefined,
              mediumVoltageCableType: undefined,
            },
          },
        });
      }
    }
  };

  const createNewCable = async () => {
    let mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    const { features } = draw.getAll();
    let img = new Image();
    img.width = 100;
    img.height =
      features[0].properties.cableType === "aerial" ||
      features[0].properties.cableType === "mediumVoltageCable"
        ? 100
        : 20;
    img.src = `data:image/svg+xml,${encodeURIComponent(
      ReactDOMServer.renderToString(
        <EquipGenSymbol property={features[0].properties} />
      )
    )}`;
    img.onload = (event: any) => {
      if (mapEvent.hasImage(`icon-${features[0].id}`)) {
        mapEvent.removeImage(`icon-${features[0].id}`);
        mapEvent.addImage(`icon-${features[0].id}`, img);
      } else {
        mapEvent.addImage(`icon-${features[0].id}`, img);
      }
    };

    const equipLayer = {
      id: `equip-${features[0].id}`,
      type: "line",
      source: {
        type: "geojson",
        data: features[0],
      },
      paint: {
        "line-pattern": `icon-${features[0].id}`,
        "line-width":
          features[0].properties.cableType === "aerial" ||
          features[0].properties.cableType === "mediumVoltageCable"
            ? 10
            : 3,
      },
    };

    const autoAnnoSourceId = uuidv4();
    const middleLine = turfAlong(features[0], turfLength(features[0]) / 2);
    const autoAnnoSource: Feature = {
      type: "Feature",
      id: autoAnnoSourceId,
      properties: {
        equip_type: "anno-label",
        text: features[0].properties.comment,
        "text-font": "Kanit",
        "text-max-width": 100,
        "text-allow-overlap": true,
        id: autoAnnoSourceId,
        "text-size": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          0,
          0,
          scaleTextToZoomLevel(500),
          24,
        ],
        "text-rotate": 0,
        "text-color": "#000000",
      },
      geometry: middleLine.geometry,
    };

    const autoAnnoLayer = {
      id: `anno-label-${autoAnnoSourceId}`,
      type: "symbol",
      source: {
        type: "geojson",
        data: autoAnnoSource,
      },
      layout: {
        "text-field": "{text}",
        "text-pitch-alignment": "auto",
        "text-rotation-alignment": "map",
        "text-font": ["Kanit"],
        "text-max-width": 100,
        "text-allow-overlap": true,
        "text-size": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          0,
          0,
          scaleTextToZoomLevel(500),
          24,
        ],
        "text-rotate": 0,
      },
      paint: {
        "text-color": "#000000",
      },
    };

    if (Boolean(mapEvent.getLayer(equipLayer.id))) {
      mapEvent.getSource(equipLayer.id).setData(equipLayer.source.data);
      mapEvent.setPaintProperty(
        equipLayer.id,
        "line-width",
        features[0].properties.cableType === "aerial" ||
          features[0].properties.cableType === "mediumVoltageCable"
          ? 10
          : 3
      );
      mapEvent.setLayoutProperty(equipLayer.id, "visibility", "visible");
    } else {
      mapEvent.addLayer(equipLayer, `gl-draw-polygon-fill-inactive.cold`);
      features[0].properties.comment &&
        features[0].properties.comment.length > 0 &&
        mapEvent.addLayer(autoAnnoLayer, `gl-draw-polygon-fill-inactive.cold`);
    }

    const hasEquip = event.designEquips.findIndex(
      (dE: any) => dE.id === equipLayer.id
    );

    draw.deleteAll();

    setevent({
      ...event,
      type: undefined,
      feature: undefined,
      designEquips:
        hasEquip >= 0
          ? event.designEquips.map((eq: any, index: number) => {
              if (index === hasEquip) {
                eq = equipLayer;
              }
              return eq;
            })
          : [...event.designEquips, equipLayer],
    });
  };

  const _cancel = () => {
    const mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    const hasDesignEq = designEquips.find(
      (eq: any) => eq.id === `equip-${feature?.id}`
    );
    if (Boolean(hasDesignEq)) {
      map?.setLayoutProperty(`equip-${feature?.id}`, "visibility", "visible");
    }
    draw.deleteAll();
    setevent({ ...event, type: undefined, feature: undefined });
  };

  return (
    <Fragment>
      <Grid container spacing={2} style={{ width: "100%" }}>
        <Grid item xs={12} className={classes.gridSave}>
          <IconButton size="small" onClick={createNewCable}>
            <Done className={classes.successColor} />
          </IconButton>
          <IconButton size="small" onClick={_cancel}>
            <Clear color="error" />
          </IconButton>
        </Grid>
        <Grid item xs={12} className={classes.flexMiddle}>
          <Typography
            variant="subtitle1"
            className={clsx(classes.title, classes.flexCenter)}
          >
            {lang.data.cable}
          </Typography>
          <div className={classes.flexCenter}>
            {feature && feature.properties && (
              <EquipGenSymbol property={feature.properties} />
            )}
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <div className={classes.equipSetting}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="subtitle1"
                style={{ marginTop: 8, marginBottom: 8 }}
              >
                {lang.data.appearance}
              </Typography>
              <div className={classes.flexMiddle}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setappeashow(!appeashow);
                  }}
                >
                  <ExpandMoreRounded />
                </IconButton>
              </div>
            </div>
            <Collapse in={appeashow}>
              <EquipDesignAppearance />
            </Collapse>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignWorkfunction />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" className={classes.flexCenter}>
              {lang.data.cableType}
            </Typography>
            {CableTypeList.map((ct) =>
              ct.value === "aerial" ? (
                <Fragment key={ct.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          feature && feature.properties?.cableType === ct.value
                        )}
                        onChange={_toggleOption}
                        name={ct.value}
                        color="primary"
                      />
                    }
                    label={ct.label}
                  />
                  <Collapse
                    in={Boolean(
                      feature && feature.properties?.cableType === "aerial"
                    )}
                  >
                    <RadioGroup
                      aria-label="aerial-size"
                      name="aerialSize"
                      value={
                        feature?.properties?.aerialSize
                          ? feature?.properties?.aerialSize
                          : CableAerialValueList[0].value
                      }
                      className={classes.radioSize}
                      onChange={_toggleRadio}
                    >
                      {CableAerialValueList.map((cl) => (
                        <FormControlLabel
                          key={cl.value}
                          value={cl.value}
                          control={<Radio color="primary" />}
                          className={classes.radioDiv}
                          label={cl.label}
                        />
                      ))}
                    </RadioGroup>
                  </Collapse>
                </Fragment>
              ) : ct.value === "mediumVoltageCable" ? (
                <Fragment key={ct.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          feature && feature.properties?.cableType === ct.value
                        )}
                        onChange={_toggleOption}
                        name={ct.value}
                        color="primary"
                      />
                    }
                    label={ct.label}
                  />
                  <Collapse
                    in={Boolean(
                      feature &&
                        feature.properties?.cableType === "mediumVoltageCable"
                    )}
                  >
                    <RadioGroup
                      aria-label="mediumVoltageCable-type"
                      name="mediumVoltageCableType"
                      value={
                        feature?.properties?.mediumVoltageCableType
                          ? feature?.properties?.mediumVoltageCableType
                          : mediumVoltageCableTypeList[0].value
                      }
                      className={classes.radioSize}
                      onChange={_toggleRadio}
                    >
                      {mediumVoltageCableTypeList.map((cl) => (
                        <FormControlLabel
                          key={cl.value}
                          value={cl.value}
                          control={<Radio color="primary" />}
                          className={classes.radioDiv}
                          label={cl.label}
                        />
                      ))}
                    </RadioGroup>
                  </Collapse>
                </Fragment>
              ) : (
                <FormControlLabel
                  key={ct.value}
                  control={
                    <Checkbox
                      checked={Boolean(
                        feature && feature.properties?.cableType === ct.value
                      )}
                      onChange={_toggleOption}
                      name={ct.value}
                      color="primary"
                    />
                  }
                  label={ct.label}
                />
              )
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignComment />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignImages />
        </Grid>
      </div>
    </Fragment>
  );
};

export default EquipDesignCable;
